import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import UserAvatar from './UserAvatar';
import SyntheseSession from '../phases/SyntheseSession';

const UsersList = React.memo(({ users, title, icon, emptyMessage }) => {
  return (
    <div className="participants-section">
      <h3 className="participants-section-title">
        <span className="section-icon">{icon}</span>
        <span className="section-title">{title}</span>
        <span className="section-count">({users.length})</span>
      </h3>
      
      {users.length === 0 ? (
        <p className="empty-section">{emptyMessage}</p>
      ) : (
        <div className="participants-grid-horizontal">
          {users.map((user, index) => (
            <div key={user.id} className="participant-item-horizontal">
              <UserAvatar user={user} size={32} />
              <div className="participant-info">
                <span className="participant-name">{user.name}</span>
                <span className="participant-role-icon">
                  {user.role === 0 && '🖊️'}
                  {user.role === 1 && '⭐'}
                  {user.role === 2 && '👁️'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.users) === JSON.stringify(nextProps.users) &&
         prevProps.title === nextProps.title &&
         prevProps.icon === nextProps.icon &&
         prevProps.emptyMessage === nextProps.emptyMessage;
});

UsersList.displayName = 'UsersList';

const SlidePanels = React.memo(({ 
  user, 
  usersInRoom, 
  redaction, 
  sujet, 
  pastContributions, 
  parametres, 
  type, 
  redactionLongue,
  setModalContent,
  setModalTitle,
  setShowModal,
  isWaitingRoom = false
}) => {
  const [showSummaryPanel, setShowSummaryPanel] = useState(false);
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);

  const handleToggleSummary = useCallback(() => {
    setShowParticipantsPanel(false);
    setShowSummaryPanel(prev => !prev);
  }, []);

  const handleToggleParticipants = useCallback(() => {
    setShowSummaryPanel(false);
    setShowParticipantsPanel(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('toggleSummaryPanel', handleToggleSummary);
    window.addEventListener('toggleParticipantsPanel', handleToggleParticipants);

    return () => {
      window.removeEventListener('toggleSummaryPanel', handleToggleSummary);
      window.removeEventListener('toggleParticipantsPanel', handleToggleParticipants);
    };
  }, [handleToggleSummary, handleToggleParticipants]);

  const closeAllPanels = useCallback(() => {
    setShowSummaryPanel(false);
    setShowParticipantsPanel(false);
  }, []);

  const { readyUsers, waitingUsers, spectators, evaluators } = useMemo(() => {
    const ready = isWaitingRoom 
      ? usersInRoom.filter(user => user.isReady)
      : usersInRoom.filter(user => 
          (redaction ? user.role === 0 : (user.role === 0 || user.role === 1)) && user.isReady
        );
    
    const waiting = isWaitingRoom
      ? usersInRoom.filter(user => !user.isReady)
      : usersInRoom.filter(user => 
          (redaction ? user.role === 0 : (user.role === 0 || user.role === 1)) && !user.isReady
        );
    
    const specs = usersInRoom.filter(user => user.role === 2);
    const evals = usersInRoom.filter(user => user.role === 1);
    
    return { readyUsers: ready, waitingUsers: waiting, spectators: specs, evaluators: evals };
  }, [usersInRoom, isWaitingRoom, redaction]);

  const anyPanelOpen = showSummaryPanel || showParticipantsPanel;

  return (
    <>
      <RemoveScroll enabled={anyPanelOpen}>
        <div 
          className={`overlay ${anyPanelOpen ? 'active' : ''}`} 
          onClick={closeAllPanels}
        />

        <div className="panels-container">
          {/* Panneau de synthèse */}
          <div className={`slide-panel summary-panel ${showSummaryPanel ? 'open' : ''}`}>
            <div className="panel-header">
              <h2 className="panel-title">📋 Synthèse de la session</h2>
              <button className="btn btn-ghost btn-icon-only" onClick={closeAllPanels}>
                ✖️
              </button>
            </div>
            <div className="panel-content">
              {!isWaitingRoom && (
                <>
                  <div className="panel-section">
                    <h3 className="panel-section-title">📝 SUJET DE LA SESSION</h3>
                    <p className="panel-subject">{sujet}</p>
                  </div>
                  
                  {pastContributions && pastContributions.length > 0 && (
                    <div className="panel-section">
                      <h3 className="panel-section-title">🏆 FORMULATIONS RETENUES</h3>
                      <div className="panel-synthesis">
                        <SyntheseSession
                          sujet={sujet}
                          premieres_contributions={pastContributions}
                          setModalContent={setModalContent}
                          setModalTitle={setModalTitle}
                          setShowModal={setShowModal}
                          parametres={parametres}
                          type={type}
                          redactionLongue={redactionLongue}
                          redactionLonguePourTop={redactionLongue}
                        />
                      </div>
                    </div>
                  )}
                  
                  {(!pastContributions || pastContributions.length === 0) && (
                    <div className="panel-section">
                      <div className="empty-state">
                        <div className="empty-icon">📝</div>
                        {!redaction ? (
                          <>
                            <p className="empty-title">Évaluation en cours</p>
                            <p className="empty-description">Les contributions retenues apparaîtront après l'évaluation</p>
                          </>
                        ) : (
                          <>
                            <p className="empty-title">Aucune contribution pour le moment</p>
                            <p className="empty-description">Les contributions apparaîtront ici au fur et à mesure de la session</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Panneau des participants */}
          <div className={`slide-panel participants-panel ${showParticipantsPanel ? 'open' : ''}`}>
            <div className="panel-header">
              <h2 className="panel-title">👥 Participants</h2>
              <button className="btn btn-ghost btn-icon-only" onClick={closeAllPanels}>
                ✖️
              </button>
            </div>
            <div className="panel-content">
              <div className="participants-sections">
                <UsersList
                  users={readyUsers}
                  title={isWaitingRoom ? 'Participants prêts' : (redaction ? 'Rédacteurs prêts' : 'Utilisateurs prêts')}
                  icon="✅"
                  emptyMessage="Aucun utilisateur prêt"
                />
                
                <UsersList
                  users={waitingUsers}
                  title={isWaitingRoom ? 'Participants en attente' : (redaction ? 'Rédacteurs en attente' : 'Utilisateurs en attente')}
                  icon="⏳"
                  emptyMessage="Tous les utilisateurs sont prêts"
                />
                
                {!isWaitingRoom && redaction && evaluators.length > 0 && (
                  <UsersList
                    users={evaluators}
                    title="Évaluateurs"
                    icon="⭐"
                    emptyMessage="Aucun évaluateur"
                  />
                )}
                
                {spectators.length > 0 && (
                  <UsersList
                    users={spectators}
                    title="Spectateurs"
                    icon="👁️"
                    emptyMessage="Aucun spectateur"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </RemoveScroll>
    </>
  );
}, (prevProps, nextProps) => {
  return prevProps.user.id === nextProps.user.id &&
         JSON.stringify(prevProps.usersInRoom) === JSON.stringify(nextProps.usersInRoom) &&
         prevProps.redaction === nextProps.redaction &&
         prevProps.sujet === nextProps.sujet &&
         JSON.stringify(prevProps.pastContributions) === JSON.stringify(nextProps.pastContributions) &&
         JSON.stringify(prevProps.parametres) === JSON.stringify(nextProps.parametres) &&
         prevProps.type === nextProps.type &&
         prevProps.redactionLongue === nextProps.redactionLongue &&
         prevProps.isWaitingRoom === nextProps.isWaitingRoom;
});

SlidePanels.displayName = 'SlidePanels';

export default SlidePanels;